import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { mainNavigation } from '@/mocks/navigation';
import { siteSettings } from '@/mocks/siteSettings';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleNavClick = useCallback(() => {
    scrollToTop();
  }, [scrollToTop]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/' || location.pathname === '/home';
    return location.pathname.startsWith(href);
  };

  const logoUrl = 'https://storage.readdy-site.link/project_files/5b7aaa34-9bab-4986-a5b7-652c9dac38d9/d581acb6-9267-427a-b6c8-a5f1d0836255_compressed_LOGO_-1.webp';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'navbar-scrolled'
          : 'bg-transparent'
      }`}
      role="navigation"
      aria-label="主要導覽"
    >
      <div className={`w-full transition-all duration-300 ${scrolled ? 'px-4 md:px-8' : 'px-4 md:px-10'}`}>
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-14' : 'h-16'}`}>
          {/* Logo + Brand */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group flex-shrink-0"
            aria-label="複刻文化首頁"
            onClick={handleNavClick}
          >
            <img
              src={logoUrl}
              alt="複刻文化 Fuko"
              className={`object-contain rounded-sm flex-shrink-0 transition-all duration-300 ${scrolled ? 'w-7 h-7' : 'w-9 h-9'}`}
              style={{ filter: scrolled ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.06))' : 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }}
            />
            <span className={`font-heading font-semibold whitespace-nowrap transition-all duration-300 text-foreground-900 ${
              scrolled ? 'text-sm' : 'text-base'
            }`}
            >
              {siteSettings.brandName}
              <span className={`font-display italic font-normal ml-1.5 transition-all duration-300 ${
                scrolled ? 'text-xs' : 'text-sm'
              }`}
              style={{ color: 'oklch(var(--primary-600))' }}
              >
                {siteSettings.brandNameEn}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Decorative dot before nav */}
            <span className="w-1 h-1 rounded-full flex-shrink-0 mr-2" style={{ backgroundColor: 'oklch(var(--primary-400)/0.45)' }} />
            {mainNavigation.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={handleNavClick}
                className={`transition-all duration-300 rounded-md whitespace-nowrap nav-link-underline ${
                  scrolled ? 'px-2.5 py-1.5 text-[13px]' : 'px-3 py-2 text-sm'
                } font-medium ${
                  isActive(item.href)
                    ? 'text-primary-600 bg-primary-50 active'
                    : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                <LocalizedNavLabel href={item.href} fallback={item.label} />
              </Link>
            ))}
            {/* Decorative dot after nav */}
            <span className="w-1 h-1 rounded-full flex-shrink-0 ml-2" style={{ backgroundColor: 'oklch(var(--primary-400)/0.45)' }} />
          </div>

          <LanguageSwitcher />

          {/* Mobile hamburger */}
          <button
            className={`lg:hidden flex items-center justify-center rounded-md transition-colors cursor-pointer ${scrolled ? 'w-9 h-9 hover:bg-background-100' : 'w-10 h-10'} text-foreground-800`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? '關閉選單' : '開啟選單'}
            aria-expanded={isOpen}
          >
            <i className={`${isOpen ? 'ri-close-line' : 'ri-menu-line'} ${scrolled ? 'text-lg' : 'text-xl'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 space-y-1">
            {mainNavigation.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                onClick={() => { setIsOpen(false); scrollToTop(); }}
                className={`block px-3 py-3 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <LocalizedNavLabel href={item.href} fallback={item.label} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}